import { useState, useEffect, useMemo } from 'react';
import { Command } from 'cmdk';
import { Search, BookOpen, FileText, File, X } from 'lucide-react';
import { useStore } from '../store/useStore';

interface SearchResultItem {
  id: string;
  type: 'asignatura' | 'nota' | 'material';
  title: string;
  subtitle?: string;
  detail?: string;
  targetView: 'asignaturas' | 'notas' | 'roadmap';
  targetId?: string;
}

export function CommandPalette() {
  const asignaturas = useStore(s => s.asignaturas);
  const notasGenerales = useStore(s => s.notasGenerales);
  const materiales = useStore(s => s.materiales);
  const navigateTo = useStore(s => s.navigateTo);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Escuchar shortcut de teclado Cmd+K o Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelectResult = (item: SearchResultItem) => {
    setIsOpen(false);
    setQuery('');
    if (item.type === 'asignatura') {
      navigateTo({ view: 'asignaturas', asignaturaId: item.targetId });
    } else if (item.type === 'nota') {
      navigateTo({ view: 'notas', asignaturaId: item.targetId });
    } else if (item.type === 'material') {
      navigateTo({ view: 'roadmap', asignaturaId: item.targetId });
    }
  };

  const asignaturasItems = useMemo(() => {
    return asignaturas
      .filter(a => !a.en_papelera)
      .map(a => ({
        id: `asig_${a.id}`,
        type: 'asignatura' as const,
        title: a.nombre,
        subtitle: a.codigo ? `Código: ${a.codigo} • ${a.creditos} CR` : `${a.creditos} CR`,
        detail: a.departamento || 'Asignatura',
        targetView: 'asignaturas' as const,
        targetId: a.id,
      }));
  }, [asignaturas]);

  const notasItems = useMemo(() => {
    return notasGenerales
      .filter(n => !n.en_papelera)
      .map(n => ({
        id: `nota_${n.id}`,
        type: 'nota' as const,
        title: n.titulo || 'Nota sin título',
        subtitle: n.tags && n.tags.length > 0 ? n.tags.map(t => `#${t}`).join(' ') : 'Apunte personal',
        detail: 'Nota',
        targetView: 'notas' as const,
        targetId: n.id,
      }));
  }, [notasGenerales]);

  const materialesItems = useMemo(() => {
    return materiales.map(m => ({
      id: `mat_${m.id}`,
      type: 'material' as const,
      title: m.nombre,
      subtitle: m.tags ? `Tags: ${m.tags}` : `Archivo ${m.tipo.toUpperCase()}`,
      detail: 'Material de estudio',
      targetView: 'roadmap' as const,
      targetId: m.asignatura_id,
    }));
  }, [materiales]);

  return (
    <Command.Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
      label="Búsqueda rápida"
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-150"
    >
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-slate-200/80 overflow-hidden flex flex-col animate-in zoom-in-95 duration-150">
        {/* Input Header */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 bg-slate-50/50">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder="Buscar asignaturas, notas o materiales en todo el sistema... (⌘K)"
            className="flex-1 bg-transparent border-none text-xs font-semibold text-slate-800 placeholder:text-slate-400 outline-none"
          />
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Cerrar búsqueda"
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Listado de Resultados */}
        <Command.List className="max-h-96 overflow-y-auto p-2 space-y-2">
          <Command.Empty className="p-8 text-center text-slate-400 text-xs font-medium">
            No se encontraron coincidencias para "{query}".
          </Command.Empty>

          {/* Grupo Asignaturas */}
          {asignaturasItems.length > 0 && (
            <Command.Group heading="Asignaturas" className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
              {asignaturasItems.map(item => (
                <Command.Item
                  key={item.id}
                  value={`${item.title} ${item.subtitle}`}
                  onSelect={() => handleSelectResult(item)}
                  className="flex items-center justify-between p-2.5 rounded-xl cursor-pointer select-none text-xs data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary transition-colors my-0.5"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <BookOpen size={14} />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-slate-900 truncate tracking-tight">{item.title}</h4>
                      <p className="text-[11px] text-slate-500 font-mono truncate">{item.subtitle}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md shrink-0">
                    {item.detail}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* Grupo Notas */}
          {notasItems.length > 0 && (
            <Command.Group heading="Notas y Apuntes" className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider px-2 py-1 mt-2">
              {notasItems.map(item => (
                <Command.Item
                  key={item.id}
                  value={`${item.title} ${item.subtitle}`}
                  onSelect={() => handleSelectResult(item)}
                  className="flex items-center justify-between p-2.5 rounded-xl cursor-pointer select-none text-xs data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary transition-colors my-0.5"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                      <FileText size={14} />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-slate-900 truncate tracking-tight">{item.title}</h4>
                      <p className="text-[11px] text-slate-500 font-mono truncate">{item.subtitle}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md shrink-0">
                    {item.detail}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* Grupo Materiales */}
          {materialesItems.length > 0 && (
            <Command.Group heading="Materiales de Estudio" className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider px-2 py-1 mt-2">
              {materialesItems.map(item => (
                <Command.Item
                  key={item.id}
                  value={`${item.title} ${item.subtitle}`}
                  onSelect={() => handleSelectResult(item)}
                  className="flex items-center justify-between p-2.5 rounded-xl cursor-pointer select-none text-xs data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary transition-colors my-0.5"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                      <File size={14} />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-slate-900 truncate tracking-tight">{item.title}</h4>
                      <p className="text-[11px] text-slate-500 font-mono truncate">{item.subtitle}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md shrink-0">
                    {item.detail}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>

        {/* Footer shortcuts */}
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between text-[11px] text-slate-400 font-mono">
          <div className="flex items-center gap-3">
            <span>↑ ↓ Navegar</span>
            <span>↵ Abrir</span>
            <span>ESC Cerrar</span>
          </div>
          <span>Malla</span>
        </div>
      </div>
    </Command.Dialog>
  );
}

